import React, { createContext, useContext, useMemo } from "react";
import { useQueries } from "react-query";
import { resolveVersion } from "./dataVersion";

interface CrosshairsDataContextValue {
  version: string;
}

const CrosshairsDataContext = createContext<CrosshairsDataContextValue | null>(null);

export interface CrosshairsDataResult {
  data: Record<string, any> | null;
  isLoading: boolean;
  error: Error | null;
}

export function getCrosshairsDataBasePath(version: string): string {
  return `${__DATA_BASE__}/data/${version}/${__SITE_SLUG__}`;
}

export const useDataVersion = (): string => {
  const ctx = useContext(CrosshairsDataContext);

  if (!ctx) {
    throw new Error("useDataVersion must be used within CrosshairsDataProvider");
  }

  return ctx.version;
};

export const CrosshairsDataProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const version = useMemo(() => resolveVersion(), []);

  return <CrosshairsDataContext.Provider value={{ version }}>{children}</CrosshairsDataContext.Provider>;
};

export function useCrosshairsData(keys: string[]): CrosshairsDataResult {
  const version = useDataVersion();
  const basePath = useMemo(() => getCrosshairsDataBasePath(version), [version]);
  const queries = useQueries(
    keys.map(key => ({
      queryKey: ["generated-page-data", version, key],
      queryFn: async () => {
        const response = await fetch(`${basePath}/${key}.json`);

        if (!response.ok) {
          throw new Error(`Failed to load ${key}.json (${response.status})`);
        }

        return response.json();
      },
      staleTime: Infinity,
      cacheTime: Infinity,
      retry: 0,
    })),
  );

  const isLoading = queries.some(query => query.isLoading);
  const error = queries.find(query => query.error)?.error ?? null;
  const allLoaded = keys.length > 0 && queries.every(query => query.isSuccess);
  const data = useMemo(() => {
    if (!allLoaded) {
      return null;
    }

    const result: Record<string, any> = {};

    keys.forEach((key, index) => {
      result[key] = queries[index].data;
    });

    return result;
  }, [allLoaded, keys, queries]);

  return {
    data,
    isLoading,
    error: error instanceof Error ? error : error ? new Error(String(error)) : null,
  };
}
